/**
 * The Book sheet's slot rules (REQUIREMENTS.md §3 "Slot states the scheduler sees"), as pure functions
 * so the API, the app and the tests agree.
 *
 * What the processor does (reference/definition_body.json, read 30 Sep 2026): a booking lands at the
 * **first free position** 1…base+1 (the 6th is surge) — it never reads RequestedCapacityPosition. A
 * position is taken while an appointment on that day holds it (ReservationState Reserved / Acquiring).
 * A proposal holds no position; when the tenant confirms it is inked at the next free one.
 *
 * So the chips are drawn the way the day will really fill:
 *   taken    — ink that is confirmed or booked directly (and any visit already started or closed):
 *              grey, locked, the unit shown; only a Reschedule frees it.
 *   waiting  — ink still waiting for the tenant's answer (Scheduled + TenantConfirmationStatus Pending):
 *              takeable, with a warning; the line is cancelled and the person goes to Needs a new day.
 *   proposed — a pencil line, drawn into the free positions from the end of the day backwards (so the
 *              free chips come first and a booking lands exactly where the first free chip is);
 *              takeable, with a warning, only when no free chip is left.
 *   free     — nobody there. Only the first free chip is pressable: that is where the booking lands.
 * A takeable chip is offered only when taking it changes where the booking lands (docs/DECISIONS.md
 * D-0.3.0-4) — nobody is displaced for nothing.
 *
 * Pick a spot (1.4.0, Queue 1 v8j, setting HK_PICK_SPOT_ENABLED): the processor books the position the
 * request names. Then every free chip can be pressed and lands where it is; a waiting line can be taken
 * for its own time (a warning and a second press, as before); pencils are still taken only when no free
 * chip is left. Until the setting is on, the rules above stand.
 */

export type ChipState = 'free' | 'taken' | 'waiting' | 'proposed';

/** One appointment on the day, as the preview reads it. */
export interface DayLine {
  appointmentId: string;
  tenantId: string;
  tenantName: string;
  unit: string;
  status: string;
  confirmation: string;
  position: number | null;
  /** Holds its position (ReservationState Reserved or Acquiring). */
  reserved: boolean;
  /** Staff unit or facility space (never takeable). */
  kind: 'tenant' | 'staff' | 'facility';
  /** SharePoint item id: pencils are drawn in the order they were made. */
  id: number;
  /** Asked for a deep clean when booked (RequestSnapshotJSON form.deepClean). */
  deep?: boolean;
}

export interface PositionChip {
  position: number;
  state: ChipState;
  /** Past the day's base capacity: the surge position (crew of four). */
  surge: boolean;
  /** Can be pressed. */
  selectable: boolean;
  /** Where the booking lands if this chip is chosen (null when not selectable). */
  lands: number | null;
  line?: Pick<DayLine, 'appointmentId' | 'tenantId' | 'tenantName' | 'unit' | 'status' | 'confirmation'>;
}

export interface DayPositions {
  chips: PositionChip[];
  /** Where a booking lands with no displacement (null: the day is full). */
  firstFree: number | null;
  /** Visits holding a position on the day (the six-visit count before this booking). */
  inkCount: number;
  /** Pencil lines that did not fit on a chip (more proposals than free positions). */
  extraProposals: DayLine[];
  deepCount: number;
}

/** "J. Alvarez" style: the initial of the first name and the last name. */
export function shortName(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return full.trim();
  return `${parts[0]!.charAt(0)}. ${parts.slice(1).join(' ')}`;
}

/** Base positions + the surge position (the processor books 1…base+1). */
export function positionCount(baseCapacity: number): number {
  return Math.max(1, baseCapacity) + 1;
}

export const PICK_SPOT_SETTING = 'HK_PICK_SPOT_ENABLED';

/** The pick-a-spot switch (SM_Settings HK_PICK_SPOT_ENABLED, default No). */
export function pickSpotOn(settings: Record<string, string | undefined> | undefined): boolean {
  return /^(yes|true|on|1)$/i.test(String(settings?.[PICK_SPOT_SETTING] ?? '').trim());
}

export function dayPositions(lines: DayLine[], baseCapacity: number, opts: { pickAny?: boolean } = {}): DayPositions {
  const pickAny = !!opts.pickAny;
  const n = positionCount(baseCapacity);
  const base = Math.max(1, baseCapacity);
  const ink = new Map<number, DayLine>();
  for (const l of lines) {
    if (l.reserved && l.position !== null && l.position >= 1 && !ink.has(l.position)) ink.set(l.position, l);
  }
  const pencils = lines.filter((l) => l.status === 'Proposed').sort((a, b) => a.id - b.id);
  const open: number[] = [];
  for (let p = 1; p <= n; p++) if (!ink.has(p)) open.push(p);
  const firstFree = open[0] ?? null;

  // Pencils fill the open positions from the end of the day backwards.
  const pencilAt = new Map<number, DayLine>();
  const extra: DayLine[] = [];
  const backwards = [...open].reverse();
  pencils.forEach((l, i) => {
    const p = backwards[i];
    if (p === undefined) extra.push(l);
    else pencilAt.set(p, l);
  });
  const freeChips = open.filter((p) => !pencilAt.has(p));

  const chips: PositionChip[] = [];
  for (let p = 1; p <= n; p++) {
    const surge = p > base;
    const i = ink.get(p);
    if (i) {
      const waiting = i.kind === 'tenant' && i.status === 'Scheduled' && i.confirmation === 'Pending';
      const selectable = waiting && (pickAny || firstFree === null || p < firstFree);
      chips.push({ position: p, state: waiting ? 'waiting' : 'taken', surge, selectable, lands: selectable ? p : null, line: pick(i) });
      continue;
    }
    const pen = pencilAt.get(p);
    if (pen) {
      const selectable = freeChips.length === 0;
      chips.push({ position: p, state: 'proposed', surge, selectable, lands: selectable ? (pickAny ? p : firstFree) : null, line: pick(pen) });
      continue;
    }
    const selectable = pickAny || (p === freeChips[0] && p === firstFree);
    chips.push({ position: p, state: 'free', surge, selectable, lands: selectable ? p : null });
  }
  const active = lines.filter((l) => l.reserved && l.position !== null);
  return { chips, firstFree, inkCount: ink.size, extraProposals: extra, deepCount: active.filter((l) => l.deep).length };
}

function pick(l: DayLine): PositionChip['line'] {
  return { appointmentId: l.appointmentId, tenantId: l.tenantId, tenantName: l.tenantName, unit: l.unit, status: l.status, confirmation: l.confirmation };
}

/** The chip a new Book sheet starts on: the first free position (none when only takeable chips are left). */
export function defaultChip(d: DayPositions): PositionChip | null {
  return d.chips.find((c) => c.state === 'free' && c.selectable) ?? null;
}

/**
 * The 6th visit (1.4.0): only a person who books (never the crew), never for today, and only with a time
 * label the scheduler types ("3:00, with the 5th") — a label, not a rule; it may overlap another visit.
 */
export const SIXTH_LABEL_MAX = 60;
export function sixthVisitProblem(a: { position: number | null; baseCapacity: number; dayKey: string; todayKey: string; timeLabel?: string | null }): 'same-day' | 'needs-label' | null {
  if (!a.position || a.position <= Math.max(1, a.baseCapacity)) return null;
  if (a.dayKey <= a.todayKey) return 'same-day';
  return (a.timeLabel ?? '').trim() ? null : 'needs-label';
}

/** Booking settings (SM_Settings, Category Housekeeping) and their defaults (REQUIREMENTS.md §3, 29 Sep). */
export const BOOKING_SETTING_DEFAULTS = {
  /** The six-visit warning: a warning, never a cap. */
  HK_WARN_AT: '6',
  /** …on a day that already carries a deep clean. */
  HK_WARN_AT_DEEP_DAY: '5',
  /** A deep clean's length (minutes, 2 h–4 h 30 in half hours; 1.5.0 default 3 h — the live 180 stays). */
  HK_DEEP_LENGTH: '180',
  /** A staff unit's visit when nobody chose a length (minutes; 1.6.4 — the August rule: real ~90-minute work). */
  HK_STAFF_LENGTH: '90',
  /** An office's visit when nobody chose a length (minutes; 1.7.8 — "offices are 30 minutes, sometimes an hour"). */
  HK_OFFICE_LENGTH: '30',
} as const;
export type BookingSettingKey = keyof typeof BOOKING_SETTING_DEFAULTS;

export function bookingSetting(settings: Record<string, string | undefined> | undefined, key: BookingSettingKey): number {
  const v = Number(settings?.[key]);
  return Number.isFinite(v) && v > 0 ? v : Number(BOOKING_SETTING_DEFAULTS[key]);
}

/** Visits a day can still take: base+1 positions, a deep clean using one more than a visit. */
export function visitsStillFit(d: DayPositions, baseCapacity: number): number {
  return Math.max(0, positionCount(baseCapacity) - d.inkCount - d.deepCount);
}

/** The count that triggers the six-visit warning on this day. */
export function warnAtFor(d: Pick<DayPositions, 'deepCount'>, settings?: Record<string, string | undefined>): number {
  return d.deepCount > 0 ? bookingSetting(settings, 'HK_WARN_AT_DEEP_DAY') : bookingSetting(settings, 'HK_WARN_AT');
}
